import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, View, Text, Spinner } from "tamagui";

import SkinV2Screen from "@components/layout/SkinV2Screen";
import { v2 } from "@screen/Home/pages/GasShop/skin";
import { colors } from "@styles/colors";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { buildXContextHeader } from "@utils/api/headers";
import { IdentityContext } from "@utils/store/user/useUser";

import SectionLabel from "./components/ui/SectionLabel";
import ListContainer from "./components/ui/ListContainer";
import InfoNote from "./components/ui/InfoNote";
import { listAccountPayments, type AccountPaymentItem } from "./API/handleAccountHistoryAPI";
import { isOrderAmountHidden } from "@utils/func/orderAmountVisibility";

/** 月份分組（含小計）資料結構 */
interface MonthGroup {
  key: string;
  items: AccountPaymentItem[];
  subtotal: number;
  count: number;
}

const PAGE_LIMIT = 20;

/** YYYY/MM 月份 key（本地時間） */
const monthKey = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/** MM/DD HH:mm（本地時間） */
const fmt = (iso: string): string => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * 依 created_at 月份（本地）分組，回傳 group 陣列。
 * items 已由後端以 created_at DESC 排序，這裡保留首次出現順序即為月份遞減。
 * 隱藏金額中的待確認訂單不計入月小計——否則可從小計差值反推被隱藏的金額。
 */
const groupByMonth = (items: AccountPaymentItem[]): MonthGroup[] => {
  const order: string[] = [];
  const map = new Map<string, MonthGroup>();
  for (const it of items) {
    const key = monthKey(it.created_at);
    const amount = isOrderAmountHidden(it.hide_amount_on_pending, it.order_status) ? 0 : (it.amount ?? 0);
    const existing = map.get(key);
    if (existing) {
      map.set(key, {
        ...existing,
        items: [...existing.items, it],
        subtotal: existing.subtotal + amount,
        count: existing.count + 1,
      });
    } else {
      order.push(key);
      map.set(key, { key, items: [it], subtotal: amount, count: 1 });
    }
  }
  return order.map((k) => map.get(k) as MonthGroup);
};

/**
 * 帳戶付款紀錄 + 月結頁。
 * 透過 GET /app-api/platform-orders/account-payments（需 X-Context）載入全部付款，
 * 客端依 created_at 月份分組並即時加總出「月結小計」。
 */
export const AccountPaymentRecords = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { contextId } = useParams();
  const { cusJwtToken } = useCusAuth();

  const ctx = (location.state as { context?: IdentityContext } | null)?.context ?? null;
  const xContext = buildXContextHeader(ctx) ?? (contextId ? `context:${contextId}` : "");

  const [items, setItems] = useState<AccountPaymentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);

  useEffect(() => {
    if (!cusJwtToken || !xContext) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    listAccountPayments({ jwtToken: cusJwtToken, xContext, params: { limit: PAGE_LIMIT } })
      .then((res) => {
        if (!active) return;
        if (res.state === "success") {
          setItems(res.data.items);
          setCursor(res.data.next_cursor);
          setHasMore(res.data.has_more);
        } else {
          console.log("account payments err", res.error);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cusJwtToken, xContext]);

  const loadMore = () => {
    if (!cusJwtToken || !xContext || !cursor || loadingMore) return;
    setLoadingMore(true);
    listAccountPayments({ jwtToken: cusJwtToken, xContext, params: { limit: PAGE_LIMIT, cursor } })
      .then((res) => {
        if (res.state === "success") {
          setItems((prev) => [...prev, ...res.data.items]);
          setCursor(res.data.next_cursor);
          setHasMore(res.data.has_more);
        } else {
          console.log("account payments load more err", res.error);
        }
      })
      .finally(() => setLoadingMore(false));
  };

  const back = () => navigate(`/account/detail/${contextId}`, { state: { context: ctx } });

  const vendor = ctx?.primary_supplier?.supplier_name ?? "瓦斯行";
  const groups = groupByMonth(items);

  const subHeader = (
    <YStack mb={16}>
      <Text color={v2.hazeSub} fontSize={13}>
        {vendor}
      </Text>
    </YStack>
  );

  return (
    /* band="short"：帶上只有一行瓦斯行名稱 */
    <SkinV2Screen title="付款紀錄" onBack={back} subHeader={subHeader} band="short">
      <YStack className="gas-stagger-kids" gap="$3.5">
        {loading ? (
          <YStack f={1} ai="center" jc="center" py="$8">
            <Spinner size="large" color={colors.primary} />
          </YStack>
        ) : items.length === 0 ? (
          <YStack f={1} ai="center" jc="center" py="$8">
            <Text fontSize={14} color={v2.body}>
              目前沒有付款紀錄
            </Text>
          </YStack>
        ) : (
          <>
            {groups.map((g, gi) => {
              // 頁首改成淡霧底之後，第一個月份的標題不必再轉白（原本是為了壓在實藍上）
              const onBand = false;
              return (
              <YStack key={g.key}>
                <SectionLabel
                  tone={onBand ? "light" : "dark"}
                  title={g.key}
                  right={
                    <Text
                      fontSize={12}
                      fontWeight="700"
                      color={onBand ? "rgba(255,255,255,0.9)" : v2.title}
                      style={{ fontVariantNumeric: "tabular-nums" }}>
                      {g.count} 筆 · $ {g.subtotal.toLocaleString()}
                    </Text>
                  }
                />
                <ListContainer>
                  {g.items.map((p, i) => {
                    // 付款方式文案由後端決定（電子支付／現金／貨到付款／度數計費），前端不再自己判斷。
                    // 過去寫死 payment_status === "cash" 只蓋到貨到付款，會計改收現金（cash_fallback）
                    // 的單會落到 fallback 顯示原本的信用卡卡號。
                    const statusText = p.payment_method_label ?? p.order_status_label;
                    return (
                      <XStack
                        key={p.platform_order_id}
                        ai="center"
                        gap="$2.5"
                        px="$3.5"
                        py="$3"
                        bbw={i === g.items.length - 1 ? 0 : 1}
                        bbc={v2.divider}>
                        <YStack f={1} gap="$1">
                          <Text fontSize={14} fontWeight="700" color={v2.title} numberOfLines={1}>
                            {p.supplier_name ?? "瓦斯行"}
                          </Text>
                          <Text fontSize={11} color={v2.sub} numberOfLines={1}>
                            {p.display_id ?? ""} · {fmt(p.created_at)}
                          </Text>
                        </YStack>
                        <YStack ai="flex-end" gap="$1" flexShrink={0}>
                          {!isOrderAmountHidden(p.hide_amount_on_pending, p.order_status) && (
                            <Text fontSize={15} fontWeight="800" color={v2.title} style={{ fontVariantNumeric: "tabular-nums" }}>
                              $ {(p.amount ?? 0).toLocaleString()}
                            </Text>
                          )}
                          <Text fontSize={11} color={v2.sub} numberOfLines={1}>
                            {statusText}
                          </Text>
                        </YStack>
                      </XStack>
                    );
                  })}
                </ListContainer>
              </YStack>
              );
            })}

            {hasMore && (
              <XStack
                ai="center"
                jc="center"
                gap="$1.5"
                bg={v2.card}
                br={999}
                py="$3"
                cursor={loadingMore ? "default" : "pointer"}
                opacity={loadingMore ? 0.6 : 1}
                pressStyle={{ opacity: 0.7 }}
                style={{ boxShadow: "0 2px 10px -4px rgba(21, 101, 192, 0.10)" }}
                onPress={loadMore}>
                {loadingMore && <Spinner size="small" color={colors.primary} />}
                <Text fontSize={13} fontWeight="700" color={v2.primary}>
                  {loadingMore ? "載入中…" : "載入更多"}
                </Text>
              </XStack>
            )}

            <InfoNote tone="primary">
              金額以下單當下凍結值為準；月結小計為本頁已載入資料的即時加總。
            </InfoNote>
          </>
        )}
      </YStack>
    </SkinV2Screen>
  );
};

export default AccountPaymentRecords;
